import {Injectable} from '@angular/core';
import moment from "moment-timezone";
import {
  HarbourDuesCargoHandlingCountForm,
  HarbourDuesDeclarationStatus,
  HarbourDuesDeclarationTab,
  HarbourDuesGoodsItemForm
} from "../types";
import {
  BerthVisit,
  DeclarationStatus,
  HarbourDuesBerthVisit,
  HarbourDuesCargoHandlingCount,
  HarbourDuesDeclaration,
  HarbourDuesDeclarationSummary,
  HarbourDuesEquipmentLength,
  HarbourDuesEquipmentType,
  HarbourDuesGoodsItem,
  PurposeCode,
  TaskMessageStatus,
  Visit
} from "@portbase/bezoekschip-service-typescriptmodels";
import {FormBuilder, FormGroup} from "@angular/forms";
import {BehaviorSubject, filter, Subject} from "rxjs";
import {NewPortvisitUtils} from "../../../new-portvisit/new-portvisit.utils";
import {map} from "rxjs/operators";
import {AppContext} from "../../../app-context";
import {sendQuery} from "../../../common/utils";
import {VisitContext} from "../../visit-context";

@Injectable({
  providedIn: 'root'
})
export class HarbourDuesService {

  declarationStatusesSubject = new BehaviorSubject<HarbourDuesDeclarationStatus[]>([]);
  statusUpdatesSubject = new Subject<HarbourDuesDeclarationStatus>();

  getDeclarations(crn: string) {
    return sendQuery<HarbourDuesDeclaration[]>("com.portbase.bezoekschip.common.api.harbourdues.queries.FindHarbourDuesDeclarations",
      {crn: crn},
      {showSpinner: true}
    );
  }

  pushStatuses(declarations: HarbourDuesDeclaration[]) {
    let declarationStatuses = declarations.map(value => ({
      crn: value.crn,
      statementNumber: value.statementNumber,
      status: value.status,
      rejectReason: value.rejectionReason
    } as HarbourDuesDeclarationStatus));
    this.declarationStatusesSubject.next(declarationStatuses);
  }

  pushStatusUpdate(declarationUpdate: HarbourDuesDeclarationSummary) {
    if (declarationUpdate.status === TaskMessageStatus.ACCEPTED) {
      AppContext.addAlert({
        type: "visit",
        level: "success",
        content: `Harbour dues declaration for ${declarationUpdate.crn} accepted`
      });
    } else if (declarationUpdate.status === TaskMessageStatus.REJECTED) {
      AppContext.addAlert({
        type: "visit",
        level: "danger",
        content: `Harbour dues declaration for ${declarationUpdate.crn} rejected: ${declarationUpdate.rejectionReason}`
      });
    }
    let declarationStatusUpdate: HarbourDuesDeclarationStatus = {
      crn: declarationUpdate.crn,
      statementNumber: declarationUpdate.statementNumber,
      status: NewPortvisitUtils.taskMessageStatusToDeclarationStatus(declarationUpdate.status),
      rejectReason: declarationUpdate.rejectionReason
    };
    this.statusUpdatesSubject.next(declarationStatusUpdate);
    this.declarationStatusesSubject.next(this.declarationStatusesSubject.getValue()
      .filter(value => value.crn !== declarationUpdate.crn || value.statementNumber !== declarationUpdate.statementNumber)
      .concat(declarationStatusUpdate));
  }

  aggregateStatusUpdate() {
    return this.declarationStatusesSubject.asObservable()
      .pipe(filter(value => value.length > 0), map(value => value.reduce((a, b) => this.priority(a.status) >= this.priority(b.status) ? a : b)));
  }

  statusUpdates() {
    return this.statusUpdatesSubject.asObservable();
  }

  declarationTabs(crn: string, arrivalStr: string, departureStr: string) {
    let currentInvoiceStart = moment(arrivalStr);
    let currentInvoiceEnd = moment(arrivalStr).add(30, 'days');
    let departure = departureStr ? moment(departureStr) : null;
    let now = moment();
    let tabs: HarbourDuesDeclarationTab[] = [];

    for (let i = 1; i < 1000; i++) {
      if (currentInvoiceStart.isAfter(now)) {
        break;
      } else if (departure && departure.isBefore(currentInvoiceEnd)) {
        tabs.push(this.declarationTab(crn, i, currentInvoiceStart, departure));
        break;
      } else if (!departure && currentInvoiceEnd.isAfter(now)) {
        tabs.push(this.declarationTab(crn, i, currentInvoiceStart, null));
        break;
      } else {
        tabs.push(this.declarationTab(crn, i, currentInvoiceStart, currentInvoiceEnd));
        currentInvoiceStart = currentInvoiceEnd.clone();
        currentInvoiceEnd = currentInvoiceStart.clone().add(30, 'days');
      }
    }
    return tabs;
  }

  declarationTab(crn: string, statementNumber: number, start: moment.Moment, end: moment.Moment) {
    return {
      crn: crn,
      statementNumber: statementNumber,
      invoiceStart: start.format(),
      invoiceEnd: end?.format()
    }
  }

  select(tab: HarbourDuesDeclarationTab, existingDeclarations: HarbourDuesDeclaration[]) {
    return existingDeclarations.find(value => value.statementNumber == tab.statementNumber) || this.newDeclaration(tab.crn, tab.invoiceStart, tab.invoiceEnd, tab.statementNumber);
  }

  private newDeclaration(crn: string, invoiceStart: string, invoiceEnd: string, statementNumber: number): HarbourDuesDeclaration {
    return {
      crn: crn,
      statementNumber: statementNumber,
      statementVersion: 1,
      invoiceStart: invoiceStart,
      invoiceEnd: invoiceEnd,
      declarant: null,
      discount: {
        greenAward: false,
        linerService: null,
        shortSea: null,
        discountType: null
      },
      exemption: null,
      incidentals: [],
      visit: {
        ata: null,
        atd: null,
        berthVisits: [],
        carrier: null,
        clientReferenceNumber: null,
        etd: null,
        lastPortOfCall: null,
        nextPortOfCall: null,
        vesselFullLength: 0,
        vesselGrossTonnage: 0
      }
    };
  }

  syncWithVisit(declaration: HarbourDuesDeclaration, visit: Visit): HarbourDuesDeclaration {
    const portVisit = visit.visitDeclaration.portVisit;
    const newDeclaration = structuredClone(declaration);

    newDeclaration.visit.ata = portVisit.ataPort;
    newDeclaration.visit.atd = portVisit.atdPort;
    newDeclaration.visit.carrier = visit.visitDeclaration.arrivalVoyage.carrier;
    newDeclaration.visit.clientReferenceNumber = visit.visitDeclaration.clientReferenceNumber;
    newDeclaration.visit.etd = portVisit.etdPort;
    newDeclaration.visit.lastPortOfCall = visit.visitDeclaration.previousPorts[0]?.port;
    newDeclaration.visit.nextPortOfCall = visit.visitDeclaration.nextPorts[0]?.port;
    newDeclaration.visit.vesselFullLength = visit.vessel.fullLength;
    newDeclaration.visit.vesselGrossTonnage = visit.vessel.grossTonnage;

    let declarationBerthVisits = newDeclaration.visit.berthVisits;
    let syncedBerthVisits = [];
    for (const visitBerthVisit of portVisit.berthVisits) {
      let harbourDuesBerthVisit = declarationBerthVisits.find(value => value.berthVisitId === visitBerthVisit.id);
      if (this.fallsWithinInvoicePeriod(
        visitBerthVisit.ata ? visitBerthVisit.ata : visitBerthVisit.eta,
        visitBerthVisit.atd ? visitBerthVisit.atd : visitBerthVisit.etd ? visitBerthVisit.etd : moment().format(),
        newDeclaration.invoiceStart,
        newDeclaration.invoiceEnd)) {
        if (!harbourDuesBerthVisit) {
          syncedBerthVisits.push(this.newHarbourDuesBerthVisit(visitBerthVisit));
        } else {
          harbourDuesBerthVisit.ata = visitBerthVisit.ata ? visitBerthVisit.ata : visitBerthVisit.eta;
          harbourDuesBerthVisit.atd = visitBerthVisit.atd ? visitBerthVisit.atd : visitBerthVisit.etd;
          harbourDuesBerthVisit.visitPurposes = visitBerthVisit.visitPurposes;
          harbourDuesBerthVisit.berth = visitBerthVisit.berth;
          if (!harbourDuesBerthVisit.visitPurposes.includes(PurposeCode.LOADING)) {
            harbourDuesBerthVisit.loadedGoods = [];
            harbourDuesBerthVisit.loadedCargoCounts = [];
            harbourDuesBerthVisit.loadedContainerHandling = [];
          }
          if (!harbourDuesBerthVisit.visitPurposes.find(value => [PurposeCode.DISCHARGE, PurposeCode.DISCHARGE_CRUDE_OIL].includes(value))) {
            harbourDuesBerthVisit.dischargedGoods = [];
            harbourDuesBerthVisit.dischargedCargoCounts = [];
            harbourDuesBerthVisit.dischargedContainerHandling = [];
          }

          syncedBerthVisits.push(harbourDuesBerthVisit);
        }
      }
    }
    newDeclaration.visit.berthVisits = syncedBerthVisits;

    return newDeclaration;
  }

  syncWithPreviousDeclaration(newDeclaration: HarbourDuesDeclaration, existingDeclarations: HarbourDuesDeclaration[]) {
    const updatedDeclaration = structuredClone(newDeclaration);
    let previousDeclaration = existingDeclarations.find(value => value.statementNumber === newDeclaration.statementNumber - 1);

    if (!previousDeclaration) {
      return updatedDeclaration;
    }

    updatedDeclaration.discount = previousDeclaration.discount;
    updatedDeclaration.exemption = previousDeclaration.exemption;
    const lastBerthVisitPreviousDeclaration = previousDeclaration.visit.berthVisits[previousDeclaration.visit.berthVisits.length - 1];

    for (const berthVisit of newDeclaration.visit.berthVisits) {
      if (berthVisit.berthVisitId === lastBerthVisitPreviousDeclaration.berthVisitId) {
        if (berthVisit.visitPurposes.includes(PurposeCode.LOADING)) {
          berthVisit.loadedGoods = lastBerthVisitPreviousDeclaration.loadedGoods;
          berthVisit.loadedCargoCounts = lastBerthVisitPreviousDeclaration.loadedCargoCounts;
        }
        if (berthVisit.visitPurposes.includes(PurposeCode.DISCHARGE) || berthVisit.visitPurposes.includes(PurposeCode.DISCHARGE_CRUDE_OIL)) {
          berthVisit.dischargedGoods = lastBerthVisitPreviousDeclaration.dischargedGoods;
          berthVisit.dischargedCargoCounts = lastBerthVisitPreviousDeclaration.dischargedCargoCounts;
        }
      }
    }

    updatedDeclaration.visit.berthVisits = updatedDeclaration.visit.berthVisits.map(berthVisit => {
      if (berthVisit.berthVisitId !== lastBerthVisitPreviousDeclaration.berthVisitId) {
        return berthVisit;
      }

      const updatedBerthVisit = { ...berthVisit };

      if (updatedBerthVisit.visitPurposes.includes(PurposeCode.LOADING)) {
        updatedBerthVisit.loadedGoods = lastBerthVisitPreviousDeclaration.loadedGoods;
        updatedBerthVisit.loadedCargoCounts =
          lastBerthVisitPreviousDeclaration.loadedCargoCounts;
      }

      if (
        updatedBerthVisit.visitPurposes.includes(PurposeCode.DISCHARGE) ||
        updatedBerthVisit.visitPurposes.includes(PurposeCode.DISCHARGE_CRUDE_OIL)
      ) {
        updatedBerthVisit.dischargedGoods = lastBerthVisitPreviousDeclaration.dischargedGoods;
        updatedBerthVisit.dischargedCargoCounts = lastBerthVisitPreviousDeclaration.dischargedCargoCounts;
      }

      return updatedBerthVisit;
    });

    return updatedDeclaration;
  }

  newCargoCount(v: HarbourDuesCargoHandlingCount = null): FormGroup<HarbourDuesCargoHandlingCountForm> {
    let fb = new FormBuilder();
    return fb.group({
      count: fb.control(v?.count),
      empty: fb.control(!!v?.empty),
      equipmentLength: fb.control<HarbourDuesEquipmentLength>(v?.equipmentLength),
      equipmentType: fb.control<HarbourDuesEquipmentType>(v?.equipmentType)
    });
  }

  newGoodsItem(v: HarbourDuesGoodsItem = null): FormGroup<HarbourDuesGoodsItemForm> {
    let fb = new FormBuilder();
    return fb.group({
      shipToShip: fb.control(v?.shipToShip),
      goodsCodeType: fb.control(v?.goodsClassification ? 'HS' : 'UNDG'),
      goodsClassification: fb.control(v?.goodsClassification),
      dangerInformation: fb.control(v?.dangerInformation),
      cargoType: fb.control(v?.cargoType),
      weight: fb.control(v?.weight),
      portOfHandling: fb.control(v?.portOfHandling)
    });
  }

  private fallsWithinInvoicePeriod(arrival: string, departure: string, invoiceStart: string, invoiceEnd: string) {
    if (!arrival || !departure) {
      return false;
    } else if (!invoiceEnd) {
      return moment(arrival).isSameOrBefore(moment(invoiceStart).clone().add('days', 30)) && moment(departure).isSameOrAfter(moment(invoiceStart));
    }
    return moment(arrival).isSameOrBefore(moment(invoiceEnd)) && moment(departure).isSameOrAfter(moment(invoiceStart));
  }

  private newHarbourDuesBerthVisit(berthVisit: BerthVisit): HarbourDuesBerthVisit {
    return {
      ata: berthVisit.ata ? berthVisit.ata : berthVisit.eta,
      atd: berthVisit.atd ? berthVisit.atd : berthVisit.etd,
      berth: berthVisit.berth,
      berthVisitId: berthVisit.id,
      dischargedCargoCounts: [],
      dischargedContainerHandling: [],
      dischargedGoods: [],
      loadedCargoCounts: [],
      loadedContainerHandling: [],
      loadedGoods: [],
      stevedore: null,
      visitPurposes: berthVisit.visitPurposes
    }
  }

  private priority(declarationStatus: DeclarationStatus): number {
    switch (declarationStatus) {
      case DeclarationStatus.REJECTED:
        return 3;
      case DeclarationStatus.DECLARED:
        return 2;
      case DeclarationStatus.ACCEPTED:
        return 1;
      default:
        return 0;
    }
  }

  applyStatusUpdate(declarations: HarbourDuesDeclaration[],
                    selected: HarbourDuesDeclaration,
                    update: HarbourDuesDeclarationStatus): void {
    declarations
      .concat(selected)
      .filter(d => d.crn === update.crn && d.statementNumber === update.statementNumber)
      .forEach(d => {
        d.rejectionReason = update.rejectReason;
        d.status = update.status;
      });
  }

  getPort() {
    if (VisitContext.savedVisit.portOfCall.port.locationUnCode == "NLAMS") {
      return "Amsterdam";
    } else if (VisitContext.savedVisit.portOfCall.port.locationUnCode == "NLRTM") {
      return "Rotterdam";
    }
  }
}
